// One visit to an article, and when it counts as read. The open article counts as read after
// READ_AFTER on screen; leaving sooner leaves it unread. Meanwhile it is "pending" (shown as read),
// and Keep unread during the wait simply cancels the pending read. A visit starts once per article
// opened, never on a re-render, so a Keep unread holds. An article opened from the address before
// any list had its row waits for the row within the same visit. The article the page opened on is
// read when the address was reached afresh (a link), and left as it was after a refresh or
// Back/Forward (kept unread, as a rule). Kept apart from React so its rules can be tested.

export interface Clock {
  /** the article's row as shown (pending changes applied), or undefined while it is not loaded */
  row: (id: string) => { isRead: boolean } | undefined;
  pending: () => string | null;
  setPending: (id: string | null) => void;
  markRead: (id: string) => void;
  /** calls back on every change in the app's state; returns how to stop */
  onChange: (fn: () => void) => () => void;
  visible?: () => boolean;
  onVisibilityChange?: (fn: () => void) => () => void;
  /** the article the page opened on, and whether it was reached afresh; cleared once used */
  landing: { id: string | null; fresh: boolean };
  after: number;
}

/** Starts a visit to `id`; returns how to end it (leaving the article). */
export function startVisit(id: string, c: Clock): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let waiting: (() => void) | undefined;
  const arm = () => {
    clearTimeout(timer);
    if (c.pending() !== id || c.visible?.() === false) return;
    timer = setTimeout(() => { if (c.pending() === id) { c.setPending(null); c.markRead(id); } }, c.after);
  };
  const stopWatchingVisibility = c.onVisibilityChange?.(arm);
  const begin = () => {
    // the article the page opened on: read when reached afresh, left as it was after a refresh
    if (c.landing.id === id) { c.landing.id = null; if (!c.landing.fresh) return; }
    if (c.row(id)?.isRead) return;
    c.setPending(id);
    arm();
  };
  if (c.row(id)) begin();
  else waiting = c.onChange(() => { if (c.row(id)) { waiting?.(); waiting = undefined; begin(); } });
  return () => {
    waiting?.();
    stopWatchingVisibility?.();
    clearTimeout(timer);
    if (c.pending() === id) c.setPending(null);
  };
}
